import React, { useState } from 'react';
import axios from 'axios';
import { Footprints, Plus, Trash2, Loader } from 'lucide-react';
import { useBulleAuSurvol } from '../BulleSurvol';

/**
 * Le parcours de prospect : les gestes posés chez un concurrent pour déclencher ses signaux.
 *
 * ## ⚠️ Pourquoi ce panneau est la moitié qui compte
 *
 * Sans geste consigné, un levier jamais relevé ne dit **rien**. Avec un geste consigné depuis
 * plus de deux semaines, le même silence devient un **constat** : « j'ai visité son site il y a
 * trois semaines et je n'ai jamais vu de retargeting — il n'en fait probablement pas ».
 *
 * C'est aussi lui qui donne l'autre borne du délai. Le protocole demande « le délai depuis ta
 * visite » : on consigne l'instant du geste, et le délai se calcule — le faire saisir le
 * rendrait faux dès le lendemain.
 *
 * ⚠️ **Un geste est rattaché à son auteur.** Deux membres de l'agence suivent des parcours
 * différents chez le même concurrent, et le délai d'une observation ne se mesure que contre les
 * gestes de celui qui l'a vue. Le panneau les affiche donc par observateur.
 */
const ParcoursProspect = ({ concurrent, token, gestes, parcours, saisieAutorisee, onChange }) => {
  const { survoler, quitter, bulle } = useBulleAuSurvol();

  const [ouvert, setOuvert] = useState(false);
  const [geste, setGeste] = useState('VISITE_SITE');
  const [faitLe, setFaitLe] = useState(() => {
    // ⚠️ Heure locale : `toISOString` daterait de la veille avant 1 h du matin.
    const d = new Date();
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  });
  const [detail, setDetail] = useState('');
  const [enregistrement, setEnregistrement] = useState(false);
  const [erreur, setErreur] = useState(null);

  const libelleGeste = (cle) => gestes.find(g => g.cle === cle)?.libelle || cle;
  const aideGeste = (cle) => gestes.find(g => g.cle === cle)?.aide || '';

  const enregistrer = async () => {
    if (!saisieAutorisee || enregistrement) return;
    setEnregistrement(true);
    setErreur(null);
    try {
      await axios.post(`/api/competitors/${concurrent.id}/parcours`,
        { geste, faitLe: new Date(faitLe).toISOString(), detail: detail || null },
        { headers: { Authorization: `Bearer ${token}` } });
      setDetail('');
      setOuvert(false);
      onChange?.();
    } catch (e) {
      const r = e.response?.data;
      setErreur((r?.fautes || [])[0] || r?.error || "L'enregistrement a échoué.");
    } finally {
      setEnregistrement(false);
    }
  };

  const supprimer = async (id) => {
    if (!saisieAutorisee) return;
    try {
      await axios.delete(`/api/competitors/${concurrent.id}/parcours/${id}`,
        { headers: { Authorization: `Bearer ${token}` } });
      onChange?.();
    } catch (e) {
      setErreur("La suppression a échoué.");
    }
  };

  const champ = { padding: '8px 10px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '13px' };

  // Groupés par observateur : c'est la lecture qui a du sens, puisque le délai s'y mesure.
  const parObservateur = new Map();
  for (const p of parcours) {
    const nom = p.observateur?.name || p.observateur?.email || p.observateurId;
    if (!parObservateur.has(nom)) parObservateur.set(nom, []);
    parObservateur.get(nom).push(p);
  }

  return (
    <div style={{
      padding: '20px', borderRadius: '12px', backgroundColor: '#fff',
      border: '1px solid #e5e7eb', marginBottom: '20px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
        <div>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px', fontWeight: '600' }}>
            <Footprints size={16} />
            Parcours de prospect
          </h3>
          <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px', maxWidth: '620px', lineHeight: 1.5 }}>
            Les gestes que vous avez posés chez ce concurrent. Ils donnent la borne de départ des
            délais, et ce sont eux qui rendent une absence de signal interprétable.
          </p>
        </div>
        {saisieAutorisee && (
          <button onClick={() => setOuvert(o => !o)} style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '7px 12px', borderRadius: '8px', border: '1px solid #d1d5db',
            backgroundColor: '#fff', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap'
          }}>
            <Plus size={13} /> Déclarer un geste
          </button>
        )}
      </div>

      {ouvert && saisieAutorisee && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end',
          padding: '14px', borderRadius: '8px', backgroundColor: '#f9fafb', marginTop: '14px'
        }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>Geste</label>
            <select value={geste} onChange={e => setGeste(e.target.value)} style={champ}>
              {gestes.map(g => <option key={g.cle} value={g.cle}>{g.libelle}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>Fait le</label>
            <input type="datetime-local" value={faitLe} onChange={e => setFaitLe(e.target.value)} style={champ} />
          </div>
          <div style={{ flex: 1, minWidth: '180px' }}>
            <label style={{ display: 'block', fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>Détail (facultatif)</label>
            <input value={detail} onChange={e => setDetail(e.target.value)}
              placeholder="fiche produit « acrylique 500 ml »" style={{ ...champ, width: '100%', boxSizing: 'border-box' }} />
          </div>
          <button onClick={enregistrer} disabled={enregistrement} style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '9px 16px', borderRadius: '8px', border: 'none',
            backgroundColor: '#3b82f6', color: '#fff', fontSize: '13px', cursor: 'pointer'
          }}>
            {enregistrement && <Loader size={13} className="spin" />} Enregistrer
          </button>
          <p style={{ width: '100%', fontSize: '11px', color: '#9ca3af', margin: 0, lineHeight: 1.4 }}>
            {aideGeste(geste)}
          </p>
        </div>
      )}

      {erreur && (
        <p style={{ fontSize: '12px', color: '#991b1b', marginTop: '10px' }}>{erreur}</p>
      )}

      {parcours.length === 0 ? (
        <p style={{ fontSize: '13px', color: '#9ca3af', marginTop: '14px', lineHeight: 1.5 }}>
          Aucun geste consigné. Tant qu'il n'y en a pas, un levier jamais relevé reste
          « non observé » — on ne peut pas distinguer « il ne le fait pas » de « on n'a rien tenté ».
        </p>
      ) : (
        <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {[...parObservateur.entries()].map(([nom, liste]) => (
            <div key={nom}>
              <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '6px' }}>{nom}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {liste.map(p => (
                  <span
                    key={p.id}
                    className="ksi-survolable"
                    onMouseEnter={survoler(
                      `${libelleGeste(p.geste)}\n${new Date(p.faitLe).toLocaleString('fr-FR')}`
                      + (p.detail ? `\n\n${p.detail}` : '')
                    )}
                    onMouseLeave={quitter}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      padding: '5px 10px', borderRadius: '999px',
                      backgroundColor: '#f3f4f6', fontSize: '12px'
                    }}
                  >
                    {libelleGeste(p.geste)}
                    <span style={{ color: '#9ca3af' }}>
                      {new Date(p.faitLe).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                    </span>
                    {saisieAutorisee && (
                      <button onClick={() => supprimer(p.id)} title="Retirer ce geste" style={{
                        border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, display: 'flex'
                      }}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {bulle}
    </div>
  );
};

export default ParcoursProspect;
